/**
 * Typewriter text (05 §10): segments are typed in order, each into its own span so headings
 * can be styled. Runs on the screen's timers, so leaving the screen stops it. A click, or
 * `finish()`, completes it at once; reduced-motion users get the whole text immediately.
 * Screen readers get the full text from the start through `aria-label`.
 */

import './chrome.css';
import type { Timers } from '../dom';
import { el } from '../dom';

export interface TypeSegment {
  text: string;
  className?: string;
}

export interface TypewriterOptions {
  /** Characters per tick and milliseconds per tick; the default is a brisk teleprinter. */
  chars?: number;
  tickMs?: number;
  instant?: boolean;
  onDone?: () => void;
}

export interface Typewriter {
  readonly el: HTMLElement;
  /** Show everything now. */
  finish(): void;
  isDone(): boolean;
}

function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function typewriter(
  segments: readonly TypeSegment[],
  timers: Timers,
  options: TypewriterOptions = {},
): Typewriter {
  const chars = Math.max(1, options.chars ?? 3);
  const tickMs = Math.max(1, options.tickMs ?? 16);
  const root = el('div', 'menu-type menu-prose');
  root.setAttribute('aria-label', segments.map((s) => s.text).join(''));
  const caret = el('span', 'menu-type-caret', '_');
  caret.setAttribute('aria-hidden', 'true');
  const spans = segments.map((s) => {
    const span = el('span', s.className ?? '');
    span.setAttribute('aria-hidden', 'true');
    return span;
  });
  root.append(...spans, caret);

  let segment = 0;
  let offset = 0;
  let done = false;

  const complete = (): void => {
    if (done) return;
    done = true;
    spans.forEach((span, i) => (span.textContent = segments[i]?.text ?? ''));
    caret.remove();
    options.onDone?.();
  };

  const tick = (): void => {
    if (done) return;
    let budget = chars;
    while (budget > 0) {
      const current = segments[segment];
      const span = spans[segment];
      if (current === undefined || span === undefined) {
        complete();
        return;
      }
      const take = Math.min(budget, current.text.length - offset);
      offset += take;
      budget -= take;
      span.textContent = current.text.slice(0, offset);
      if (offset >= current.text.length) {
        segment++;
        offset = 0;
      }
    }
    if (segment >= segments.length) complete();
    else timers.later(tickMs, tick);
  };

  root.addEventListener('click', complete);
  if (options.instant === true || prefersReducedMotion()) complete();
  else timers.later(tickMs, tick);

  return { el: root, finish: complete, isDone: () => done };
}
